import { FC } from 'react'

import { clsx } from 'clsx'

import { BackgroundProvider } from '../BackgroundContext/index.js'
import type { IconComponentType } from '../Icon/types.js'
import { XIcon } from '../icons/index.js'
import { ToastProvider, ToastStatus, useToastContext } from './ToastContext.js'
import { ToastIcon } from './ToastIcon.js'

/**
 * Toast panel, dynamically insert this into the live region when it needs to be displayed
 */
export type ToastComponentProps = React.ComponentProps<'div'> & {
  status?: ToastStatus
  onClose: () => void
}

/**
 * Toast panel, dynamically insert this into the live region when it needs to be displayed
 */
export const ToastComponent = ({
  onClose,
  status = 'neutral',
  children,
  className,
  ...rest
}: ToastComponentProps) => {
  return (
    <div
      className={clsx(
        'max-w-sm w-full shadow-lg rounded-lg overflow-hidden pointer-events-auto border border-line-subdued',
        'bg-surface text-content-subdued',
        'focus-visible:ring-1 focus:outline-none',
        className,
      )}
      {...rest}
    >
      <ToastProvider onClose={onClose} status={status}>
        <BackgroundProvider backgroundType="surface">
          {children}
        </BackgroundProvider>
      </ToastProvider>
    </div>
  )
}

export type ToastContentProps = React.ComponentProps<'div'> & {
  title?: string
  description?: string
  withClose?: boolean
  icon?: IconComponentType
}
const ToastContent: FC<ToastContentProps> = props => {
  const {
    children,
    title,
    description,
    icon,
    withClose = true,
    className,
    ...rest
  } = props

  const { status, onClose } = useToastContext()

  return (
    <div
      className={clsx(
        'p-4 relative',
        [
          status === 'neutral' && 'bg-surface text-content-subdued',
          status === 'error' && 'bg-surface-negative text-content-on-negative',
          status === 'warning' &&
            'bg-surface-attention text-content-on-attention',
          status === 'success' &&
            'bg-surface-positive text-content-on-positive',
          status === 'info' && 'bg-surface-highlight text-content-on-highlight',
        ],
        className,
      )}
      {...rest}
    >
      <div className="flex items-start text-sm ">
        <div className="flex-shrink-0">
          <ToastIcon status={status}>{icon}</ToastIcon>
        </div>
        <div className={clsx('ms-3 flex-1', withClose && 'me-8')}>
          {title && <p className="font-bold">{title}</p>}
          {description && <p className="mt-1">{description}</p>}
          {children}
        </div>
        {withClose && (
          <span className="absolute top-0 end-0 flex items-center me-4 mt-4">
            <button
              type="button"
              className="rounded-md inline-flex focus:outline-none focus-visible:ring"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <XIcon
                className={clsx('h-5 w-5', [
                  status === 'neutral' &&
                    'text-content-subdued hover:text-content-hovered',
                  status === 'error' &&
                    'text-content-on-negative hover:text-content-on-negative-hovered',
                  status === 'warning' &&
                    'text-content-on-attention hover:text-content-on-attention',
                  status === 'success' &&
                    'text-content-on-positive hover:text-content-on-positive',
                  status === 'info' &&
                    'text-content-on-highlight hover:text-content-on-highlight',
                ])}
                aria-hidden="true"
              />
            </button>
          </span>
        )}
      </div>
    </div>
  )
}

ToastComponent.Content = ToastContent
